import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { RequiredLabel, getFieldErrorSx } from '../../components/common/FormHelpers';
import { currencySelectOptions } from '../../constants/iso4217Currencies';

/** Normalize catalog UUIDs from GET /employees/:id (flat columns and/or nested associations). */
function workplaceIdsFromContact(c) {
  if (!c || typeof c !== 'object') {
    return { employee_job_role_id: '', employee_title_id: '', employee_offered_title_id: '' };
  }
  const roleId =
    c.employee_job_role_id ||
    c.employeeJobRole?.id ||
    c.employeeJobRoleId ||
    '';
  const titleId =
    c.employee_title_id || c.employeeTitle?.id || c.employeeTitleId || '';
  const offeredId =
    c.employee_offered_title_id ||
    c.employeeOfferedTitle?.id ||
    c.employeeOfferedTitleId ||
    '';
  return {
    employee_job_role_id: roleId != null ? String(roleId).trim() : '',
    employee_title_id: titleId != null ? String(titleId).trim() : '',
    employee_offered_title_id: offeredId != null ? String(offeredId).trim() : '',
  };
}

function firstValidationToastMessage(data) {
  const arr = data?.errors;
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const first = arr[0];
  if (typeof first === 'string') return first;
  if (first?.msg) return first.msg;
  if (first?.message) return first.message;
  return null;
}

const EmployeeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromMyTeam = location.pathname.startsWith('/my-team/employees');
  const employeesBase = fromMyTeam ? '/my-team/reportees' : '/employees';
  const { isAdmin } = useAuth();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const preContactId = searchParams.get('contact_id');
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eligibleContacts, setEligibleContacts] = useState([]);
  const [employeeContacts, setEmployeeContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [employeeJobRoles, setEmployeeJobRoles] = useState([]);
  const [employeeTitles, setEmployeeTitles] = useState([]);
  const [employeeOfferedTitles, setEmployeeOfferedTitles] = useState([]);
  const [departmentCatalog, setDepartmentCatalog] = useState([]);
  const [teamNameCatalog, setTeamNameCatalog] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    contact_id: preContactId || '',
    employment_type: 'fulltime',
    department: '',
    designation: '',
    job_title: '',
    team_name: '',
    date_of_joining: '',
    salary: '',
    salary_period: '',
    salary_currency: '',
    hourly_rate: '',
    hourly_currency: '',
    reporting_manager_contact_id: '',
    client_account_id: '',
    notes: '',
    employee_job_role_id: '',
    employee_title_id: '',
    employee_offered_title_id: '',
    alternate_reporting_manager_contact_id: '',
    external_employee_id: '',
  });
  const [skipReportingManager, setSkipReportingManager] = useState(false);

  const fetchEmployeeTitlesForRole = useCallback(async (roleId) => {
    if (!roleId) {
      setEmployeeTitles([]);
      return;
    }
    try {
      const response = await api.get('/contacts/employee-titles', {
        params: { employee_job_role_id: roleId },
      });
      setEmployeeTitles(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch employee levels');
      setEmployeeTitles([]);
    }
  }, []);

  const fetchEmployeeOfferedTitlesForRole = useCallback(async (roleId) => {
    if (!roleId) {
      setEmployeeOfferedTitles([]);
      return;
    }
    try {
      const response = await api.get('/contacts/employee-offered-titles', {
        params: { employee_job_role_id: roleId },
      });
      setEmployeeOfferedTitles(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch employee offered titles');
      setEmployeeOfferedTitles([]);
    }
  }, []);

  const fetchOrgNameCatalogs = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([
        api.get('/settings/catalog/department-names'),
        api.get('/settings/catalog/team-names'),
      ]);
      setDepartmentCatalog(d.data?.data || []);
      setTeamNameCatalog(t.data?.data || []);
    } catch {
      setDepartmentCatalog([]);
      setTeamNameCatalog([]);
    }
  }, []);

  useEffect(() => {
    fetchEligibleContacts();
    fetchEmployeeContacts();
    fetchAccounts();
    fetchEmployeeJobRoles();
    fetchOrgNameCatalogs();
    if (isEdit) fetchEmployee();
    else if (preContactId) setFormData((prev) => ({ ...prev, contact_id: preContactId }));
  }, [id, preContactId, isEdit, fetchOrgNameCatalogs]);

  useEffect(() => {
    if (!formData.employee_job_role_id) {
      setEmployeeTitles([]);
      setEmployeeOfferedTitles([]);
      return;
    }
    fetchEmployeeTitlesForRole(formData.employee_job_role_id);
    fetchEmployeeOfferedTitlesForRole(formData.employee_job_role_id);
  }, [formData.employee_job_role_id, fetchEmployeeTitlesForRole, fetchEmployeeOfferedTitlesForRole]);

  useEffect(() => {
    if (!formData.employee_offered_title_id) {
      setFormData((prev) => (prev.job_title ? { ...prev, job_title: '' } : prev));
      return;
    }
    const t = employeeOfferedTitles.find((x) => String(x.id) === String(formData.employee_offered_title_id));
    if (!t) return;
    setFormData((prev) => (prev.job_title === t.name ? prev : { ...prev, job_title: t.name }));
  }, [formData.employee_offered_title_id, employeeOfferedTitles]);

  useEffect(() => {
    if (isEdit || !preContactId) return;
    let cancelled = false;
    api
      .get(`/contacts/${preContactId}`)
      .then((r) => {
        const c = r.data?.data;
        if (!c || cancelled) return;
        const wp = workplaceIdsFromContact(c);
        setFormData((prev) => ({
          ...prev,
          contact_id: preContactId,
          employee_job_role_id: wp.employee_job_role_id,
          employee_title_id: wp.employee_title_id,
          employee_offered_title_id: wp.employee_offered_title_id,
          alternate_reporting_manager_contact_id: c.alternate_reporting_manager_contact_id || '',
          external_employee_id: c.external_employee_id || '',
          reporting_manager_contact_id: c.reporting_manager_contact_id || prev.reporting_manager_contact_id,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isEdit, preContactId]);

  const fetchEligibleContacts = async () => {
    try {
      const r = await api.get('/contacts?eligible_for_employee=true&status=active&limit=500');
      setEligibleContacts(r.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchEmployeeContacts = async () => {
    try {
      const r = await api.get('/contacts?contact_type=employee&status=active&limit=500');
      setEmployeeContacts(r.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAccounts = async () => {
    try {
      const r = await api.get('/accounts?account_type=hosting_company&status=active&limit=200');
      setAccounts(r.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchEmployeeJobRoles = async () => {
    try {
      const response = await api.get('/contacts/employee-job-roles');
      setEmployeeJobRoles(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch employee job roles');
    }
  };

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/employees/${id}`);
      const d = r.data.data;
      const {
        Contact,
        reportingManager,
        manager,
        User,
        reportees,
        recentTimesheets,
        clientAccount,
        assignedJob,
        ...rest
      } = d;
      const c = Contact || d.contact || {};
      const wp = workplaceIdsFromContact(c);
      setFormData((prev) => ({
        ...prev,
        ...rest,
        contact_id: d.contact_id || prev.contact_id,
        reporting_manager_contact_id: d.reporting_manager_contact_id || '',
        salary: d.salary ?? '',
        salary_period: d.salary_period ?? '',
        salary_currency: d.salary_currency ?? '',
        hourly_rate: d.hourly_rate ?? '',
        hourly_currency: d.hourly_currency ?? '',
        employee_job_role_id: wp.employee_job_role_id,
        employee_title_id: wp.employee_title_id,
        employee_offered_title_id: wp.employee_offered_title_id,
        alternate_reporting_manager_contact_id: c.alternate_reporting_manager_contact_id || '',
        external_employee_id: c.external_employee_id || '',
      }));
      if (clientAccount?.id && clientAccount.account_type !== 'hosting_company') {
        setAccounts((prev) => (prev.some((x) => x.id === clientAccount.id) ? prev : [clientAccount, ...prev]));
      }
    } catch (e) {
      toast.error('Failed to fetch');
      navigate(employeesBase);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'employee_job_role_id') {
      setFormData((prev) => ({
        ...prev,
        employee_job_role_id: value,
        employee_title_id: '',
        employee_offered_title_id: '',
        job_title: '',
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !isEdit &&
      !formData.reporting_manager_contact_id &&
      !(isAdmin && skipReportingManager)
    ) {
      toast.error('Select a reporting manager, or enable “no reporting manager” as admin for org root.');
      return;
    }
    if (!formData.employee_job_role_id) {
      setValidationErrors((prev) => ({ ...prev, employee_job_role_id: true }));
      toast.error('Employee Job Role is required.');
      return;
    }
    if (!formData.employee_title_id) {
      setValidationErrors((prev) => ({ ...prev, employee_title_id: true }));
      toast.error('Employee Level is required.');
      return;
    }
    if (!formData.employee_offered_title_id) {
      setValidationErrors((prev) => ({ ...prev, employee_offered_title_id: true }));
      toast.error('Employee Offered Title is required.');
      return;
    }
    if (!isEdit && !formData.client_account_id) {
      toast.error('Select company associated (a Hosting Company account). Add one under Accounts if the list is empty.');
      return;
    }
    if (
      formData.alternate_reporting_manager_contact_id &&
      formData.alternate_reporting_manager_contact_id === formData.reporting_manager_contact_id
    ) {
      toast.error('Alternate manager must be different from the reporting manager.');
      return;
    }

    setSaving(true);
    try {
      const offeredTitleRow = employeeOfferedTitles.find(
        (x) => String(x.id) === String(formData.employee_offered_title_id)
      );
      const jobTitleResolved =
        (offeredTitleRow?.name || formData.job_title || '').trim() || undefined;

      const roleId = String(formData.employee_job_role_id || '').trim();
      const titleId = String(formData.employee_title_id || '').trim();
      const offeredId = String(formData.employee_offered_title_id || '').trim();
      const payload = {
        employment_type: formData.employment_type,
        department: formData.department || undefined,
        designation: formData.designation || undefined,
        job_title: jobTitleResolved,
        team_name: formData.team_name || undefined,
        date_of_joining: formData.date_of_joining,
        salary: formData.salary === '' ? undefined : formData.salary,
        salary_period: formData.salary_period === '' ? null : formData.salary_period,
        salary_currency:
          formData.salary_currency === '' || formData.salary_currency == null
            ? null
            : String(formData.salary_currency).trim().toUpperCase(),
        hourly_rate: formData.hourly_rate === '' ? undefined : formData.hourly_rate,
        hourly_currency:
          formData.hourly_currency === '' || formData.hourly_currency == null
            ? null
            : String(formData.hourly_currency).trim().toUpperCase(),
        reporting_manager_contact_id: formData.reporting_manager_contact_id || null,
        client_account_id: formData.client_account_id || undefined,
        notes: formData.notes || undefined,
        employee_job_role_id: roleId,
        employee_title_id: titleId,
        employee_offered_title_id: offeredId,
        alternate_reporting_manager_contact_id: formData.alternate_reporting_manager_contact_id || null,
        external_employee_id:
          formData.external_employee_id != null && String(formData.external_employee_id).trim() !== ''
            ? String(formData.external_employee_id).trim()
            : null,
      };
      if (!isEdit) {
        payload.contact_id = formData.contact_id;
        payload.skip_reporting_manager = isAdmin && skipReportingManager;
      }
      if (isEdit) await api.put(`/employees/${id}`, payload);
      else await api.post('/employees', payload);
      toast.success(`Employee ${isEdit ? 'updated' : 'created'} successfully`);
      if (isEdit) {
        navigate(fromMyTeam ? `/my-team/employees/${id}` : `/employees/${id}`);
      } else {
        navigate('/employees');
      }
    } catch (error) {
      const data = error.response?.data;
      const detail = firstValidationToastMessage(data);
      toast.error(detail || data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const managerOptions = employeeContacts.filter((c) => c.id !== formData.contact_id);
  const selectedManager = managerOptions.find((c) => c.id === formData.reporting_manager_contact_id) || null;
  const selectedAlternate =
    managerOptions.find((c) => c.id === formData.alternate_reporting_manager_contact_id) || null;

  const departmentOptionValue = useMemo(() => {
    const d = (formData.department || '').trim();
    if (!d) return null;
    const hit = departmentCatalog.find((x) => x.name === d);
    if (hit) return hit;
    return { id: '__legacy__', name: d, legacy: true };
  }, [formData.department, departmentCatalog]);

  const teamNameOptionValue = useMemo(() => {
    const t = (formData.team_name || '').trim();
    if (!t) return null;
    const hit = teamNameCatalog.find((x) => x.name === t);
    if (hit) return hit;
    return { id: '__legacy__', name: t, legacy: true };
  }, [formData.team_name, teamNameCatalog]);

  const currencyOptions = useMemo(() => currencySelectOptions(), []);

  const jobTitleFromOffered = useMemo(() => {
    const t = employeeOfferedTitles.find((x) => String(x.id) === String(formData.employee_offered_title_id));
    return t?.name || (formData.job_title || '').trim() || '';
  }, [employeeOfferedTitles, formData.employee_offered_title_id, formData.job_title]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(employeesBase)}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? 'Edit Employee' : 'Employee HR profile'}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Employee information
            </Typography>
            <Grid container spacing={3}>
              {!isEdit && (
                <Grid item xs={12}>
                  <Autocomplete
                    options={eligibleContacts}
                    getOptionLabel={(c) =>
                      `${[c.first_name, c.last_name].filter(Boolean).join(' ')} (${c.email}) · ${c.contact_type}`
                    }
                    value={eligibleContacts.find((c) => c.id === formData.contact_id) || null}
                    onChange={(_, v) => setFormData((prev) => ({ ...prev, contact_id: v?.id || '' }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Contact"
                        required
                        helperText="Candidates or employees without an HR profile yet"
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Employment type</InputLabel>
                  <Select
                    name="employment_type"
                    value={formData.employment_type}
                    label="Employment type"
                    onChange={handleChange}
                  >
                    <MenuItem value="fulltime">Full time</MenuItem>
                    <MenuItem value="parttime">Part time</MenuItem>
                    <MenuItem value="contractor">Contractor</MenuItem>
                    <MenuItem value="intern">Intern</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={departmentCatalog}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(a, b) => Boolean(a && b && a.name === b.name)}
                  value={departmentOptionValue}
                  onChange={(_, v) =>
                    setFormData((prev) => ({ ...prev, department: v?.name != null ? String(v.name).trim() : '' }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department"
                      helperText="From Administration → Settings → Department names"
                    />
                  )}
                  noOptionsText="No departments in Settings. Add active names under Administration → Settings."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={teamNameCatalog}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(a, b) => Boolean(a && b && a.name === b.name)}
                  value={teamNameOptionValue}
                  onChange={(_, v) =>
                    setFormData((prev) => ({ ...prev, team_name: v?.name != null ? String(v.name).trim() : '' }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team name"
                      helperText="From Administration → Settings → Team names"
                    />
                  )}
                  noOptionsText="No team names in Settings. Add active names under Administration → Settings."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Designation / role code"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  helperText="Optional internal role label"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date of joining"
                  name="date_of_joining"
                  type="date"
                  value={formData.date_of_joining?.split('T')[0] || ''}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Salary" name="salary" type="number" value={formData.salary} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="emp-salary-period-label">Salary is</InputLabel>
                  <Select
                    labelId="emp-salary-period-label"
                    label="Salary is"
                    name="salary_period"
                    value={formData.salary_period || ''}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={currencyOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  value={currencyOptions.find((o) => o.code === formData.salary_currency) || null}
                  onChange={(_, v) => setFormData((p) => ({ ...p, salary_currency: v?.code || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Salary currency (ISO)" placeholder="Search code or name…" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Hourly rate" name="hourly_rate" type="number" value={formData.hourly_rate} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={8}>
                <Autocomplete
                  options={currencyOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  value={currencyOptions.find((o) => o.code === formData.hourly_currency) || null}
                  onChange={(_, v) => setFormData((p) => ({ ...p, hourly_currency: v?.code || '' }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Hourly currency (ISO)" placeholder="Search code or name…" />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Employee workplace (contact catalog)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These fields are stored on the linked contact for OKR catalog and reporting. Job role options are managed
              under Administration → Employee Settings. <strong>Job title</strong> on the HR record is taken from the
              selected <strong>Employee Offered Title</strong> below.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!validationErrors.employee_job_role_id} sx={getFieldErrorSx(!!validationErrors.employee_job_role_id)}>
                  <InputLabel>
                    <RequiredLabel>Employee Job Role</RequiredLabel>
                  </InputLabel>
                  <Select name="employee_job_role_id" value={formData.employee_job_role_id || ''} label="Employee Job Role" onChange={handleChange}>
                    {employeeJobRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!validationErrors.employee_title_id} sx={getFieldErrorSx(!!validationErrors.employee_title_id)}>
                  <InputLabel>
                    <RequiredLabel>Employee Level</RequiredLabel>
                  </InputLabel>
                  <Select
                    name="employee_title_id"
                    value={formData.employee_title_id || ''}
                    label="Employee Level"
                    onChange={handleChange}
                    disabled={!formData.employee_job_role_id}
                  >
                    {employeeTitles.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!validationErrors.employee_title_id}>
                    {validationErrors.employee_title_id ? 'Employee Level is required' : 'Internal level for the selected job role.'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  required
                  error={!!validationErrors.employee_offered_title_id}
                  disabled={!formData.employee_job_role_id}
                  sx={getFieldErrorSx(!!validationErrors.employee_offered_title_id)}
                >
                  <InputLabel>
                    <RequiredLabel>Employee Offered Title</RequiredLabel>
                  </InputLabel>
                  <Select
                    name="employee_offered_title_id"
                    value={formData.employee_offered_title_id || ''}
                    label="Employee Offered Title"
                    onChange={handleChange}
                  >
                    {employeeOfferedTitles.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!validationErrors.employee_offered_title_id}>
                    {validationErrors.employee_offered_title_id
                      ? 'Employee Offered Title is required'
                      : 'Negotiated title; options are under Administration → Employee Settings.'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Job title (from offered title)"
                  value={jobTitleFromOffered}
                  InputProps={{ readOnly: true }}
                  helperText="Saved on the HR profile; always matches the Employee Offered Title you select."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Payroll / external employee ID"
                  name="external_employee_id"
                  value={formData.external_employee_id}
                  onChange={handleChange}
                  placeholder="e.g. HRIS or payroll ID"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Reporting
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={managerOptions}
                  getOptionLabel={(c) => `${[c.first_name, c.last_name].filter(Boolean).join(' ')} (${c.email})`}
                  value={selectedManager}
                  onChange={(_, v) =>
                    setFormData((prev) => ({
                      ...prev,
                      reporting_manager_contact_id: v?.id || '',
                    }))
                  }
                  disabled={isAdmin && skipReportingManager}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Reporting manager"
                      required={!(isAdmin && skipReportingManager)}
                      helperText="Another contact with type Employee. Admins can use the switch below for org root only."
                    />
                  )}
                />
              </Grid>
              {!isEdit && isAdmin && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch checked={skipReportingManager} onChange={(e) => setSkipReportingManager(e.target.checked)} />
                    }
                    label="No reporting manager (org root only)"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Autocomplete
                  options={managerOptions}
                  getOptionLabel={(c) => `${[c.first_name, c.last_name].filter(Boolean).join(' ')} (${c.email})`}
                  value={selectedAlternate}
                  onChange={(_, v) =>
                    setFormData((prev) => ({
                      ...prev,
                      alternate_reporting_manager_contact_id: v?.id || '',
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Alternate manager" placeholder="Optional" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required={!isEdit}>
                  <InputLabel>{isEdit ? 'Company associated' : <RequiredLabel>Company associated</RequiredLabel>}</InputLabel>
                  <Select
                    name="client_account_id"
                    value={formData.client_account_id || ''}
                    label={isEdit ? 'Company associated' : 'Company associated *'}
                    onChange={handleChange}
                  >
                    {isEdit && <MenuItem value="">None</MenuItem>}
                    {accounts.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.company_name}
                        {a.account_code ? ` (${a.account_code})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Only <strong>Hosting Company</strong> accounts appear here (organizations hosting this tenant). New
                    employees must be associated with one. Add accounts under Administration → Accounts with type Hosting
                    Company * if this list is empty.
                  </FormHelperText>
                </FormControl>
              </Grid>
              {!isEdit && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    New HR profiles start as <strong>Pending</strong>. An administrator or the reporting manager must approve
                    before this person appears in organization employee pickers. An administrator can then create a system
                    login (or use Add user later).
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleChange} multiline rows={3} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate(employeesBase)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving || (!isEdit && !formData.contact_id)}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EmployeeForm;
